import type { AnalyticsProvider, AnalyticsEventMap } from "./types";
import { captureError } from "@/lib/error-tracking";

const providers: AnalyticsProvider[] = [];

export function registerProvider(provider: AnalyticsProvider): void {
  if (providers.some((existing) => existing.name === provider.name)) return;
  providers.push(provider);
}

function captureProviderError(
  providerName: string,
  operation: "track" | "identify" | "group" | "resetGroups" | "reset",
  error: unknown,
): void {
  captureError(error, {
    tags: {
      area: "analytics",
      provider: providerName,
      operation,
    },
  });
}

function toProviderProperties<TProperties extends object>(
  properties: TProperties,
): Record<string, unknown> {
  return Object.fromEntries(Object.entries(properties));
}

export function track<TEventName extends keyof AnalyticsEventMap>(
  event: TEventName,
  properties: AnalyticsEventMap[TEventName],
): void {
  for (const provider of providers) {
    try {
      provider.track(event, toProviderProperties(properties));
    } catch (error) {
      captureProviderError(provider.name, "track", error);
    }
  }
}

export function identify(
  userId: string,
  traits?: Record<string, unknown>,
): void {
  for (const provider of providers) {
    try {
      provider.identify(userId, traits);
    } catch (error) {
      captureProviderError(provider.name, "identify", error);
    }
  }
}

export function group(
  groupType: string,
  groupKey: string,
  traits?: Record<string, unknown>,
): void {
  for (const provider of providers) {
    if (!provider.group) continue;
    try {
      provider.group(groupType, groupKey, traits);
    } catch (error) {
      captureProviderError(provider.name, "group", error);
    }
  }
}

export function resetGroups(): void {
  for (const provider of providers) {
    if (!provider.resetGroups) continue;
    try {
      provider.resetGroups();
    } catch (error) {
      captureProviderError(provider.name, "resetGroups", error);
    }
  }
}

export function reset(): void {
  for (const provider of providers) {
    try {
      provider.reset();
    } catch (error) {
      captureProviderError(provider.name, "reset", error);
    }
  }
}
